import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/core/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-neutral-100 disabled:border-disabled-500 disabled:text-disabled-500 duration-300",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 text-sm",
        destructive:
          "bg-red-500 text-white hover:bg-red-600 text-sm font-normal",
        outline:
          "border border-secondary-200 bg-transparent hover:bg-secondary text-neutral-900 text-sm",
        outlineBlack:
          "border border-neutral-900 bg-transparent hover:bg-secondary text-neutral-900 text-sm",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 text-sm",
        ghost: "hover:bg-secondary hover:text-secondary-foreground text-sm",
        link: "text-neutral-900 underline-offset-4 hover:underline py-3 px-4 max-h-[40px] text-sm",
        green:
          "text-neutral-900 bg-complementary-500 border border-neutral-900 py-3 px-4 max-h-[40px] disabled:bg-neutral-100 disabled:border-disabled-500 disabled:text-disabled-500 hover:bg-complementary-400",
        black:
          "bg-black border border-neutral-900 hover:bg-neutral-200 hover:text-neutral-900 text-white text-sm",
        primary:
          "bg-primary-500 hover:bg-primary-500/80 text-neutral-900 text-sm disabled:bg-primary-500/50",
        light:
          "bg-white text-neutral-900 hover:bg-white/80 text-sm disabled:bg-neutral-100 disabled:border-disabled-500 disabled:text-disabled-500",
        floatingBar:
          "border-l border-neutral-400 text-nowrap rounded-none font-normal hover:bg-transparent",
        tertiary:
          "bg-tertiary-200 text-neutral-900 hover:bg-tertiary-200/80 text-sm disabled:bg-neutral-100 disabled:border-disabled-500 disabled:text-disabled-500",
        chatbot:
          "text-white px-4 py-2 bg-aiBlue-500 hover:bg-aiBlue-500/80 rounded-lg touch-manipulation font-medium",
        "outline-aiBlue":
          "py-2 px-4 gap-2 rounded-[12px] border border-aiBlue-500 bg-transparent text-aiBlue-500 text-sm font-medium hover:bg-aiBlue-100 hover:border-aiBlue-500",
        backTo: "hover:bg-secondary text-[#07329F] text-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        xs: "rounded-md h-[24px] px-2",
        lg: "h-11 rounded-md px-4 md:px-8",
        icon: "h-10 w-10",
        full: "w-full",
        floatingBar: "h-auto py-0 px-4 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
